/**
 * Entity Type Group Registry — Keyword-Tag Classifier
 *
 * Sprint 58: Replaced static ENTITY_TYPES array with a keyword-tag classifier
 * that dynamically groups any entity type. New types from imports are
 * automatically classified into business/technology/governance/uncategorised
 * without touching this file.
 *
 * OOD: Open-closed — new entity types are classified by keyword matching
 * without modifying the registry. The classifier encapsulates grouping logic.
 */

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export type GroupId = 'business' | 'technology' | 'governance';
export type ExtendedGroupId = GroupId | 'uncategorised';

export interface GroupDef {
    id: GroupId;
    label: string;
    /** Feather icon name */
    icon: string;
    /** Tailwind-compatible hex color */
    color: string;
}

// ---------------------------------------------------------------------------
// Group Definitions (unchanged from Sprint 57)
// ---------------------------------------------------------------------------

export const GROUP_DEFS: readonly GroupDef[] = [
    { id: 'business',   label: 'Business',   icon: 'briefcase', color: '#3b82f6' },
    { id: 'technology', label: 'Technology',  icon: 'cpu',       color: '#f59e0b' },
    { id: 'governance', label: 'Governance',  icon: 'shield',    color: '#ef4444' },
] as const;

export const UNCATEGORISED_GROUP_DEF: GroupDef = {
    id: 'uncategorised' as any,
    label: 'Uncategorised',
    icon: 'help-circle',
    color: '#6b7280',
};

// ---------------------------------------------------------------------------
// Keyword Tag Sets (replaces static ENTITY_TYPES array)
// ---------------------------------------------------------------------------

const GROUP_KEYWORDS: Record<GroupId, string[]> = {
    business: [
        'division', 'department', 'org', 'organization', 'role', 'hr-role', 'hr_role',
        'capability', 'process', 'process_connector', 'business-service', 'business_service',
        'unit', 'function',
    ],
    technology: [
        'application', 'app', 'tech', 'technical-service', 'technical_service', 'infra',
        'infrastructure', 'device', 'integration', 'data', 'data_object', 'data-object',
        'system', 'platform', 'database',
    ],
    governance: [
        'risk', 'control', 'compliance', 'vendor', 'audit', 'policy', 'regulation', 'security',
    ],
};

// ---------------------------------------------------------------------------
// Known Display Names (static lookup for human-friendly labels)
// ---------------------------------------------------------------------------

const KNOWN_DISPLAY_NAMES: Record<string, string> = {
    'division':          'Division',
    'department':        'Department',
    'organization':      'Organization',
    'hr-role':           'HR Role',
    'hr_role':           'HR Role',
    'capability':        'Capability',
    'process':           'Process',
    'process_connector': 'Process Connector',
    'process-connector': 'Process Connector',
    'business-service':  'Business Service',
    'business_service':  'Business Service',
    'application':       'Application',
    'technical-service': 'Technical Service',
    'technical_service': 'Technical Service',
    'infrastructure':    'Infrastructure',
    'device':            'Device',
    'integration':       'Integration',
    'data':              'Data',
    'data_object':       'Data Object',
    'data-object':       'Data Object',
    'risk':              'Risk',
    'control':           'Control',
    'compliance':        'Compliance',
    'vendor':            'Vendor',
    'audit':             'Audit',
    'policy':            'Policy',
    'regulation':        'Regulation',
    'security':          'Security',
    'system':            'System',
    'platform':          'Platform',
    'database':          'Database',
    'unit':              'Unit',
    'function':          'Function',
};

// ---------------------------------------------------------------------------
// Classifier
// ---------------------------------------------------------------------------

/**
 * Classify an entity type cyKey into a group using two-pass keyword matching:
 * 1. Exact match (including underscore/hyphen variants)
 * 2. Substring containment (does cyKey contain a keyword, or vice versa)
 * 3. Falls back to 'uncategorised'
 */
export function classifyEntityType(cyKey: string): ExtendedGroupId {
    const normalized = cyKey.toLowerCase().replace(/\s+/g, '-');
    const altForm = normalized.includes('-')
        ? normalized.replace(/-/g, '_')
        : normalized.replace(/_/g, '-');

    // Pass 1: exact match
    for (const [groupId, keywords] of Object.entries(GROUP_KEYWORDS) as [GroupId, string[]][]) {
        for (const kw of keywords) {
            if (normalized === kw || altForm === kw) {
                return groupId;
            }
        }
    }

    // Pass 2: substring containment
    for (const [groupId, keywords] of Object.entries(GROUP_KEYWORDS) as [GroupId, string[]][]) {
        for (const kw of keywords) {
            if (normalized.includes(kw) || kw.includes(normalized)) {
                return groupId;
            }
        }
    }

    return 'uncategorised';
}

// ---------------------------------------------------------------------------
// Dynamic Registry Builder
// ---------------------------------------------------------------------------

/**
 * Scan all nodes and classify each unique entityType. Returns a map from
 * entityType cyKey to its group. Both hyphen and underscore variants stored.
 */
export function buildDynamicRegistry(
    nodes: { entityType: string }[],
): Map<string, ExtendedGroupId> {
    const registry = new Map<string, ExtendedGroupId>();

    for (const node of nodes) {
        const normalized = node.entityType.toLowerCase().replace(/\s+/g, '-');
        if (registry.has(normalized)) continue;

        const group = classifyEntityType(normalized);
        registry.set(normalized, group);

        // Store alternate form too
        if (normalized.includes('-')) {
            registry.set(normalized.replace(/-/g, '_'), group);
        } else if (normalized.includes('_')) {
            registry.set(normalized.replace(/_/g, '-'), group);
        }
    }

    return registry;
}

// ---------------------------------------------------------------------------
// Display Name
// ---------------------------------------------------------------------------

/**
 * Get a human-readable display name for an entity type cyKey.
 * Uses static lookup for known names, auto-generates for unknowns:
 * 'data-object' -> 'Data Object'
 */
export function getDisplayName(cyKey: string): string {
    const normalized = cyKey.toLowerCase().replace(/\s+/g, '-');
    if (KNOWN_DISPLAY_NAMES[normalized]) return KNOWN_DISPLAY_NAMES[normalized];

    // Auto-generate: replace hyphens/underscores with spaces, title case
    return normalized
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Dynamic Group Members
// ---------------------------------------------------------------------------

export interface DynamicGroupMember {
    cyKey: string;
    displayName: string;
    count: number;
}

/**
 * Returns sorted-by-count members of a group based on actual node data.
 * Replaces static getGroupMembers().
 */
export function getDynamicGroupMembers(
    groupId: ExtendedGroupId,
    nodes: { entityType: string }[],
): DynamicGroupMember[] {
    const typeCounts = new Map<string, number>();

    for (const node of nodes) {
        const normalized = node.entityType.toLowerCase().replace(/\s+/g, '-');
        const nodeGroup = classifyEntityType(normalized);
        if (nodeGroup === groupId) {
            typeCounts.set(normalized, (typeCounts.get(normalized) || 0) + 1);
        }
    }

    const members: DynamicGroupMember[] = [];
    for (const [cyKey, count] of typeCounts.entries()) {
        members.push({
            cyKey,
            displayName: getDisplayName(cyKey),
            count,
        });
    }

    // Sort by count descending
    members.sort((a, b) => b.count - a.count);
    return members;
}

// ---------------------------------------------------------------------------
// Count Entities by Group (updated to use classifyEntityType)
// ---------------------------------------------------------------------------

/**
 * Count nodes per group from a flat array of constellation nodes.
 * Returns ExtendedGroupId counts (includes 'uncategorised').
 */
export function countEntitiesByGroup(
    nodes: { entityType: string }[],
): Map<ExtendedGroupId, number> {
    const counts = new Map<ExtendedGroupId, number>();

    for (const node of nodes) {
        const normalized = node.entityType.toLowerCase().replace(/\s+/g, '-');
        const groupId = classifyEntityType(normalized);
        counts.set(groupId, (counts.get(groupId) || 0) + 1);
    }

    return counts;
}
